/**
 * Plain-English labels, page intros, and hover hints for the dashboard.
 * Use these so non-analysts can read cards and tables without jargon.
 */

/** Short column / KPI labels */
export const L = {
  date: 'Date',
  spend: 'Ad spend',
  revenue: 'Sales',
  sales: 'Sales',
  mer: 'Sales per ad $',
  roas: 'Sales per ad $',
  aov: 'Avg order size',
  cac: 'Cost per purchase',
  ctr: 'Click rate',
  cpc: 'Cost per click',
  cpm: 'Cost per 1,000 views',
  purchases: 'Purchases',
  orders: 'Orders',
  newOrders: 'New customer orders',
  returningOrders: 'Repeat customer orders',
  impressions: 'Ad views',
  clicks: 'Clicks',
  linkClicks: 'Link clicks',
  addToCart: 'Add to cart',
  checkout: 'Started checkout',
  subRevenue: 'Subscription sales',
  rebillRevenue: 'Renewal sales',
  newSubRevenue: 'New subscription sales',
  newSubs: 'New subscriptions',
  activeSubs: 'Active subscriptions',
  converted: 'Now paying',
  cancelled: 'Cancelled',
  avgContract: 'Avg subscription price',
  attachRate: 'Attach rate',
  ttpRate: 'TTP rate (as of end date)',
  ttpRateAsOf: 'TTP rate (as of end date)',
  ttpRateInPeriod: 'TTP rate (trial ended in range)',
  activationRate: 'Activation rate (as of end date)',
  activationRateAsOf: 'Activation rate (as of end date)',
  activationRateInPeriod: 'Activation rate (trial ended in range)',
  matureInPeriod: 'Trial ended in range',
  airOrders: 'Orders with Air',
  eligibleOrders: 'Orders that could add Air',
  paidAir: 'Paid Air orders',
  zeroAir: 'Free Air orders',
  matureSubs: 'Mature subs (as of end date)',
  matureSubsAsOf: 'Mature subs (as of end date)',
  paidConversions: 'Paid conversions',
  cancels30d: 'Cancelled within 30 days',
  cancelRate30d: '30-day cancel rate',
  combinedNetRevenue: 'Total Air revenue (net)',
  day1Revenue: '1-day attributed sales',
  attributedOrders: 'Orders linked to this ad',
  attributedAirOrders: 'Air orders linked to this ad',
  attributedAirRevenue: 'Air sales linked to this ad',
  totalAttributedOrders: 'All orders linked to ads',
  blendedMer: 'Combined sales per ad $',
  nvp: 'New customer %',
  ncOrders: 'New customer orders',
  emailSent: 'Emails sent',
  emailOpened: 'Emails opened',
  emailClicked: 'Email clicks',
  emailRevenue: 'Sales from email',
  openRate: 'Open rate',
  clickRate: 'Email click rate',
  rows: 'Rows shown',
  planTarget: 'Plan / target',
  variance: 'Vs plan',
  projection: 'Full-year forecast',
};

/** One-line hints for KPI cards (hover on the number) */
export const TIP = {
  spend: 'Money you paid to run ads in the selected dates.',
  revenue: 'Money from orders in the selected dates (after discounts where noted).',
  orderRevenue: 'Gross Product Sales + Shipping + Taxes − Discounts. Triple Whale Order Revenue — used for MER.',
  mer: 'How much you earned in sales for each $1 spent on ads. Higher is better.',
  roas: 'Sales divided by ad spend for this ad or channel.',
  aov: 'Average amount per order.',
  cac: 'Ad spend divided by number of purchases.',
  ctr: 'Share of people who clicked after seeing the ad.',
  attachRate: 'Share of eligible orders in the selected dates that included the NOBL Air add-on.',
  ttpRate: 'Of all subscribers whose trial had already ended by the end date, what share are now paying. Use this for headline / forecast alignment.',
  ttpRateAsOf: 'Of all subscribers whose trial had already ended by the end date, what share are now paying. Includes the full mature base (e.g. ~67%), not only trials that ended in the selected range.',
  ttpRateInPeriod: 'Of subscribers whose trial ended during the selected dates only, what share started paying. Often lower than “as of end date” (e.g. ~63%) because recent cohorts are still converting.',
  activationRate: 'Attach rate × TTP rate (as of end date).',
  activationRateAsOf: 'Attach rate for the date range × TTP rate (as of end date). Matches the main dashboard TTP definition.',
  activationRateInPeriod: 'Attach rate for the date range × TTP rate (trial ended in range). Compare to “as of end date” to see period vs cumulative trial success.',
  combinedNetRevenue: 'Hardware + new subs + renewals, minus discounts and refunds.',
  rebillRevenue: 'Appstle successful billing attempts (cash collected). Source of truth for subscription sales.',
  newSubRevenue: 'Signed contract value for new signups (not cash collected; not added into Total).',
  activeSubs: 'Subscriptions still active right now (not cancelled).',
  activeArr: 'Estimated yearly value of all active subscriptions.',
  eligibleOrders: 'Store orders that could include Air (excludes rebills).',
  paidAir: 'Orders where the customer paid for the Air add-on.',
  zeroAir: 'Orders where Air was added at $0.',
  matureSubs: 'All subscribers whose 14-day trial had finished by the end date (denominator for TTP as of end date).',
  matureInPeriod: 'Subscribers whose trial ended during the selected dates (denominator for TTP in range).',
  paidConversions: 'Those subscribers who started paying after the trial.',
  cancels30d: 'Subscribers who cancelled within 30 days of signing up.',
  cancelRate30d: '30-day cancels divided by subscribers who finished trial.',
  day1Revenue: 'Sales Triple Whale attributes to the ad within 1 day of the click.',
  airOrders: 'Orders that included NOBL Air and luggage.',
  newSubs: 'New subscription signups in the date range.',
  converted: 'Subscribers who moved from trial to paid billing.',
  cancelled: 'Subscriptions that were cancelled.',
  avgContract: 'Average monthly subscription price.',
  totalSubRevenue: 'Appstle successful billing attempts in the date range (past-orders report).',
  subRevenue: 'Appstle successful billing attempts in the date range (past-orders report).',
  purchases: 'Number of purchase events attributed to ads.',
  amazonToplineRevenue: 'Total Amazon marketplace order revenue (Seller Central OPS basis). Included in topline Sales / Order Revenue for NOBL.',
  amazonAdsRevenue: 'Amazon Ads attributed sales when ads ran; otherwise marketplace order revenue (Seller Central). Purchases and new customers come from Amazon orders when ads conversions are unavailable.',
  amazonChannelSpend: 'Amazon Ads spend when campaigns ran; otherwise marketplace COGS + handling fees from Seller Central orders (enables ROAS/CAC on organic days).',
  shopifyRevenue: 'Shopify order revenue only (excludes Amazon marketplace).',
  impressions: 'How many times ads were shown.',
  nvp: 'Percent of orders from first-time customers.',
  ncOrders: 'Orders from someone buying for the first time.',
  emailRevenue: 'Sales attributed to email campaigns.',
  airOrders: 'Orders that included the NOBL Air add-on and luggage.',
  activeArr: 'Rough yearly total if every active subscriber kept paying.',
};

/** Page title + subtitle shown under the h1 */
export const PAGE = {
  overview: {
    title: 'Overview',
    desc: 'Both stores at a glance: sales, ad spend, orders, and how efficiently ads pay back.',
  },
  channels: {
    title: 'Channels',
    desc: 'Compare ad platforms (Meta, Google, TikTok, etc.): what you spent, what you sold, and return per dollar.',
  },
  metaAds: {
    title: 'Meta (Facebook) ads',
    desc: 'Facebook & Instagram ad results by campaign, ad set, and ad — spend, sales, and efficiency.',
  },
  subscriptions: {
    title: 'Subscriptions',
    desc: 'Subscription signups and revenue for NOBL Air and/or Pilates FLO in your selected dates.',
  },
  live: {
    title: "Today's snapshot",
    desc: 'The most recent daily numbers for your selected store — sales, ad spend, orders, regional breakdown, and channel performance at a glance.',
  },
  forecastEngine: {
    title: 'Forecast',
    desc: 'Forward-looking sales and ad efficiency vs your plan — in everyday language.',
  },
  noblAir: {
    title: 'NOBL Air performance',
    desc: 'How the Air subscription product is doing: add-ons, trials, paying subscribers, and Meta ad results. TTP and activation are shown two ways — “as of end date” (full mature base) and “trial ended in range” (selected dates only).',
  },
  storeNobl: {
    title: 'NOBL Travel',
    desc: 'Full store picture: sales, ads, regions, subscriptions, and email — US and EU combined.',
  },
  storeFlo: {
    title: 'Pilates FLO',
    desc: 'Full store picture: sales, ads, regions, subscriptions, and email.',
  },
};

/** Map legacy table header strings → plain labels (for one-off migrations) */
export const HEADER_PLAIN = {
  'Spend': L.spend,
  'Revenue': L.revenue,
  'ROAS': L.roas,
  'MER': L.mer,
  'Blended MER': L.blendedMer,
  'CAC': L.cac,
  'CTR': L.ctr,
  'CPC': L.cpc,
  'CPM': L.cpm,
  'AOV': L.aov,
  'Purchases': L.purchases,
  'Total Revenue': 'Total sales',
  'Total Spend': 'Total ad spend',
  'NOBL Revenue': 'NOBL sales',
  'NOBL Spend': 'NOBL ad spend',
  'NOBL MER': 'NOBL sales per ad $',
  'FLO Revenue': 'FLO sales',
  'FLO Spend': 'FLO ad spend',
  'FLO MER': 'FLO sales per ad $',
  'Sub Revenue': L.subRevenue,
  '7d Spend': 'Ad spend (7 days)',
  'New Orders': L.newOrders,
  'Attach Rate': L.attachRate,
  'TTP Rate': L.ttpRate,
  'Activation Rate': L.activationRate,
  'Day 1 Revenue': L.day1Revenue,
  'Total Attributed Orders': L.totalAttributedOrders,
  'Attributed Air Orders': L.attributedAirOrders,
  'Attributed Air Revenue': L.attributedAirRevenue,
  'Air Orders': L.airOrders,
  'Air-Eligible Orders': L.eligibleOrders,
  'Paid Air': L.paidAir,
  '$0 Air': L.zeroAir,
  'Combined Net Revenue': L.combinedNetRevenue,
  'Rebill Revenue': L.rebillRevenue,
  'New Sub Revenue': L.newSubRevenue,
  'Link Clicks': L.linkClicks,
  'Add To Cart': L.addToCart,
  'Checkout': L.checkout,
  'Impressions': L.impressions,
  'Clicks': L.clicks,
  'New Subs': L.newSubs,
  'Total Sub Revenue': 'Total subscription sales',
  'Rebill': 'Renewal orders',
  'Same-Day Cancel': 'Cancelled same day',
  'Mature': 'Finished trial',
  'Converted': L.converted,
  'TTP Mature Air Orders': 'Trial-ended Air orders',
  'TTP Paid Air Orders': 'Trial-ended → paid Air orders',
  'Order Revenue': 'Order sales',
  'Sub TTP%': 'Trial → paid rate (subs)',
  'Sub TTP Rate': 'Trial → paid rate',
  'Combined Net Rev': 'Total Air sales (net)',
  'Rebill Rev': 'Renewal sales',
  'Tag Net Rev': 'Tag net sales',
  'Gross Revenue': 'Gross sales',
  'Avg Order Rev': 'Avg order sales',
  'TTP Rate': 'Trial → paid rate',
  'TTP Rate %': 'Trial → paid rate',
  'vs. Avg': 'Vs average',
  'Activation %': 'Overall success rate',
  'TTP %': 'Trial → paid rate',
  'Rev / 1K': 'Sales per 1K orders',
  'Revenue / 1K': 'Sales per 1K orders',
  'Activations / 1K': 'Successes per 1K orders',
  'Avg Attach Rate': 'Avg Air add-on rate',
  'Intl Attach Rate': 'Intl Air add-on rate',
  'Subscription Revenue': 'Subscription sales',
  'Sub Revenue': 'Subscription sales',
  'Order sales': 'Order sales',
  'Store Revenue': 'Store sales',
  'Activation Rate': 'Overall success rate',
  'Est. Activations': 'Est. successes',
  'Tag Rev (net est)': 'Tag sales (est.)',
  'Sub Rev (net est)': 'Sub sales (est.)',
  'Total Air Rev (net est)': 'Total Air sales (est.)',
};

export function plainHeader(header) {
  return HEADER_PLAIN[header] || header;
}

/** Hover hint on table column headers (key = display label) */
export const COLUMN_TIP = {
  'Order Revenue': TIP.orderRevenue,
  'NOBL Order Revenue': TIP.orderRevenue,
  'Total Order Revenue': TIP.orderRevenue,
  [L.spend]: TIP.spend,
  [L.revenue]: TIP.revenue,
  [L.mer]: TIP.mer,
  [L.roas]: TIP.roas,
  [L.aov]: TIP.aov,
  [L.cac]: TIP.cac,
  [L.ctr]: TIP.ctr,
  [L.attachRate]: TIP.attachRate,
  [L.ttpRate]: TIP.ttpRate,
  [L.activationRate]: TIP.activationRate,
  [L.day1Revenue]: TIP.day1Revenue,
  'Total sales': TIP.revenue,
  'Total ad spend': TIP.spend,
};
